import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Leaf, Utensils, Milk, Wine, Croissant, Cookie, Salad } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import WordFadeIn from "@/components/WordFadeIn";
import Seo from "@/components/Seo";

const products = [
  {
    icon: Leaf,
    title: "Obst und Gemüse",
    desc: "In der Obst- und Gemüseabteilung von Rewe Kotlarski finden Sie eine Auswahl, die verschiedene Ernährungswünsche und Einkaufsanlässe berücksichtigt. Zum Angebot gehören saisonales Obst und Gemüse, beispielsweise Spargel, sowie Erzeugnisse aus der Region. Ergänzt wird das Sortiment durch Bioware, frische Kräuter und exotische Früchte. Damit verbindet die Abteilung vertraute Produkte mit besonderen Varianten und schafft eine vielseitige Grundlage für den täglichen Einkauf – ob für die schnelle Küche, die bewusste Ernährung oder neue Rezeptideen.",
  },
  {
    icon: Utensils,
    title: "Frischetheke und SB-Ware",
    desc: "Im Bereich der Frischetheke bietet Rewe Kotlarski ausschließlich SB-Ware aus dem Metzgereibereich an. Die übersichtlich präsentierten Artikel lassen sich bequem auswählen und unkompliziert in den Einkauf integrieren. Bei Fragen zum aktuellen Sortiment hilft Ihnen das Team im Markt gerne weiter.",
  },
  {
    icon: Milk,
    title: "Molkereiprodukte",
    desc: "Die Abteilung für Molkereiprodukte bei Rewe Kotlarski bietet eine vielseitige Auswahl für Frühstück, Zwischenmahlzeit, Küche und Dessert. Zum Sortiment gehören Joghurt, Pudding, Desserts, Milchdrinks, Käse, Kefir und Quark. Auch vegane und laktosefreie Alternativen sind erhältlich, darunter Joghurt, Milchdrinks, Käse und Quark. So finden Sie sowohl klassische Molkereiprodukte als auch passende Optionen für unterschiedliche Ernährungsweisen und einen individuell abgestimmten Einkauf.",
  },
  {
    icon: Wine,
    title: "Getränke",
    desc: "In der Getränkeabteilung von Rewe Kotlarski erwartet Sie ein breit aufgestelltes Sortiment für Alltag, Genuss und besondere Anlässe. Zum Angebot gehören Bier, Wein und Spirituosen ebenso wie Säfte, Wasser, Softdrinks und Schorlen. Auch regionale Getränke sind Teil der Auswahl. Wer direkt zu gekühlten Produkten greifen möchte, findet zudem gekühlte Getränke im Sortiment. Damit deckt die Abteilung unterschiedliche Vorlieben ab – von alkoholfreien Erfrischungen über klassische Durstlöscher bis zu Getränken für gesellige Momente.",
  },
  {
    icon: Croissant,
    title: "Bäckerei Wiehler",
    desc: "Im Markt befindet sich eine Bäckerei der Bäckerei Wiehler aus Meerbusch. Das Angebot umfasst Brötchen, Baguette, belegte Brötchen, Brezeln, herzhafte Snacks, süßes Gebäck und Kuchen. Ergänzend stehen Sitzmöglichkeiten sowie Coffee to go zur Verfügung. Damit bietet die Bäckerei sowohl Produkte zum Mitnehmen als auch die Möglichkeit, eine kleine Pause vor Ort einzuplanen. Ansprechpartner für die Bäckerei ist Peter Kotlarski.",
  },
  {
    icon: Cookie,
    title: "Bake-Off-Shop",
    desc: "Zusätzlich zur Bäckerei verfügt Rewe Kotlarski über einen Bake-Off-Shop, in dem Backwaren täglich frisch aufgebacken werden. Zum Angebot gehören Brötchen, Brot, Baguette und Brezeln sowie herzhafte Snacks, süßes Gebäck und Kuchen. Damit bietet der Bake-Off-Bereich eine vielseitige Auswahl für das Frühstück, die Brotzeit, eine schnelle Zwischenmahlzeit oder den kleinen Genuss zwischendurch.",
  },
  {
    icon: Salad,
    title: "Mittagsverpflegung",
    desc: "Für die Mittagsverpflegung bietet Rewe Kotlarski frische Bowls, abgepackte hausgemachte Salate und belegte Brötchen an der Frischetheke beziehungsweise beim Bäcker. Damit stehen Ihnen unkomplizierte Möglichkeiten für eine Mahlzeit oder einen Snack zur Verfügung. Die Auswahl eignet sich ideal für die Mittagspause und lässt sich bequem in den Einkauf integrieren.",
  },
];

const impressions = [
  { title: "Obst & Gemüse", image: "/images/obstbereich.webp" },
  { title: "Molkereiprodukte", image: "/images/molkerei.webp" },
  { title: "Getränke & Konserven", image: "/images/konserven.webp" },
];

const Produkte = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <>
      <Seo title="Produkte & Sortiment | Rewe Kotlarski Meerbusch" description="Saisonales Obst und Gemüse, Bio-Produkte, Molkereiwaren, Getränke sowie Backwaren der Bäckerei Wiehler und täglich frischer Bake-Off bei Rewe Kotlarski." />
      <PageHeader
        tag="Produkte"
        title="Vielfältiges Sortiment für jeden Einkauf"
        subtitle="Entdecken Sie bei Rewe Kotlarski Obst und Gemüse, Molkereiprodukte, Getränke, Backwaren und weitere Angebote."
        heroImage="/images/marktauswahl.webp"
        drawIconVariant="cart"
      />

      <section ref={ref} className="py-20 px-6 md:px-16">
        <div className="max-w-5xl mx-auto space-y-6">
          {products.map((p, i) => (
            <motion.article
              key={p.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.08 * i }}
              className="bg-card border border-border p-8 md:p-10 flex flex-col items-center text-center md:flex-row md:items-start md:text-left gap-6"
            >
              <p.icon className="w-8 h-8 text-primary-bright shrink-0" aria-hidden="true" />
              <div>
                <h2 className="font-display text-2xl font-semibold text-foreground mb-3" style={{ textWrap: "pretty" as any }}>{p.title}</h2>
                <p className="text-muted-foreground font-body leading-relaxed" style={{ textWrap: "pretty" as any }}>{p.desc}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* Market impressions */}
      <section className="py-20 px-6 md:px-16 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <p className="font-body text-sm text-primary-bright tracking-[0.3em] uppercase mb-4 text-center md:text-left">
            Ein Blick in den Markt
          </p>
          <WordFadeIn
            text="Eindrücke aus unserem Sortiment"
            as="h2"
            className="font-display text-3xl md:text-5xl font-bold text-foreground text-center md:text-left mb-16 [hyphens:none]"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {impressions.map((im, i) => (
              <motion.figure
                key={im.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 * i }}
                className="relative h-[40vh] md:h-[50vh] overflow-hidden"
              >
                <img
                  src={im.image}
                  alt={im.title}
                  className="absolute inset-0 w-full h-full object-cover object-center"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/30 to-transparent" />
                <figcaption className="relative z-10 flex items-end h-full p-6 md:p-8">
                  <h3 className="font-display text-xl md:text-2xl font-semibold text-foreground [hyphens:none]">{im.title}</h3>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Produkte;